/**
 * Push diagnostics hook (Card 17).
 *
 * Gathers the two halves — what this DEVICE knows (plugin present, permission, token, last
 * notification actually seen) and what the SERVER knows (credentials configured, registered
 * tokens, classified delivery outcomes) — and hands both to the pure resolver.
 *
 * The device half is deliberately resolved even when the server call fails. A device with no
 * push plugin in its binary must still be told so while offline; making the whole screen
 * depend on a network round-trip would reintroduce "looks idle, is broken".
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { isNative, platform } from '@/utils/platform';
import { checkPushPermissionStatus } from '@/services/capacitor/pushNotifications';
import {
  getLastDeliverySeen,
  getLocalTokenState,
  probeNativePushPrerequisites
} from '@/services/capacitor/pushDiagnostics';
import { resolvePushState } from '@/utils/pushState';
import type {
  PushDiagnosticsInput,
  PushPlatform,
  PushServerDiagnostics,
  ResolvedPushState
} from '@/types/pushDiagnostics.types';

/** Result of a one-tap self-test, as the screen needs to render it. */
export interface SelfTestResult {
  ok: boolean;
  /** 'no_devices' | 'all_failed' | 'sent' | 'error' — kept apart on purpose. */
  kind: 'sent' | 'no_devices' | 'all_failed' | 'error';
  message: string;
  outcomes: Array<{
    platform: PushPlatform;
    deviceName?: string | null;
    outcome: string;
    errorCode?: string | null;
    error?: string | null;
  }>;
}

export interface UsePushDiagnostics {
  loading: boolean;
  /** Device-side facts + server-side facts, already reduced to one state. */
  resolved: ResolvedPushState | null;
  input: PushDiagnosticsInput | null;
  server: PushServerDiagnostics | null;
  /**
   * Set when the server half could not be fetched. The screen shows this as a banner
   * rather than silently rendering a device-only view that looks complete.
   */
  serverError: string | null;
  refresh: () => Promise<void>;
  sendTest: () => Promise<SelfTestResult>;
  testing: boolean;
  lastTest: SelfTestResult | null;
}

export function usePushDiagnostics(): UsePushDiagnostics {
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState<PushDiagnosticsInput | null>(null);
  const [resolved, setResolved] = useState<ResolvedPushState | null>(null);
  const [server, setServer] = useState<PushServerDiagnostics | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState<SelfTestResult | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);

    // Device half first, and independently: it must survive a failed server call.
    const [prerequisites, permission, localToken] = await Promise.all([
      probeNativePushPrerequisites(),
      checkPushPermissionStatus(),
      getLocalTokenState()
    ]);
    const lastDelivery = getLastDeliverySeen();

    let serverData: PushServerDiagnostics | null = null;
    let error: string | null = null;

    try {
      const response = await axios.get('/api/push/diagnostics', {
        withCredentials: true,
        timeout: 15000
      });
      serverData = response.data?.data ?? null;
    } catch (err: any) {
      error =
        err?.response?.status === 403
          ? 'This account is not an admin, so server-side delivery history is hidden.'
          : err?.response?.data?.message || err?.message || 'Could not reach the diagnostics endpoint.';
    }

    const nextInput: PushDiagnosticsInput = {
      platform: platform as PushPlatform,
      isNative,
      prerequisites,
      permission,
      localToken,
      lastDelivery,
      server: serverData
    };

    setServer(serverData);
    setServerError(error);
    setInput(nextInput);
    setResolved(resolvePushState(nextInput));
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Fire a notification at this account's own devices and report exactly what happened.
   *
   * The three failure shapes are kept apart because they need opposite fixes and used to be
   * one indistinguishable "failed to send":
   *   422 — no registered device to send TO
   *   502 — devices existed and every send was rejected (bad key, dead token, quota)
   *   anything else — the request itself did not complete
   */
  const sendTest = useCallback(async (): Promise<SelfTestResult> => {
    setTesting(true);
    try {
      const response = await axios.post('/api/push/test', {}, {
        withCredentials: true,
        timeout: 20000
      });

      const data = response.data?.data ?? {};
      const result: SelfTestResult = {
        ok: true,
        kind: 'sent',
        message: `Sent to ${data.sent ?? 0} of ${data.attempted ?? 0} device(s). Watch for the banner.`,
        outcomes: data.outcomes ?? []
      };
      setLastTest(result);
      return result;
    } catch (err: any) {
      const status = err?.response?.status;
      const details = err?.response?.data?.details ?? {};
      const result: SelfTestResult = {
        ok: false,
        kind: status === 422 ? 'no_devices' : status === 502 ? 'all_failed' : 'error',
        message:
          status === 422
            ? 'No device is registered for push on this account — there was nothing to send to.'
            : err?.response?.data?.message || err?.message || 'The test request did not complete.',
        outcomes: details.outcomes ?? []
      };
      setLastTest(result);
      return result;
    } finally {
      setTesting(false);
      // The attempt itself is new evidence — re-read so the screen reflects it.
      void refresh();
    }
  }, [refresh]);

  return { loading, resolved, input, server, serverError, refresh, sendTest, testing, lastTest };
}
